import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import PropTypes from 'prop-types';

import logo from '../../assets/images/advran-logo.png';
import Paths from '../../constants/Paths';
import User from '../User';
import UserPopup from '../UserPopup';
import { Button, ButtonStyle, Icon, IconType, IconSize } from '../Utils';
import NotificationsPopup from './NotificationsPopup';

import * as s from './Header.module.scss';

const Header = React.memo(
  ({
    path,
    project,
    user,
    notifications,
    filteredNotifications,
    notificationFilter,
    notificationCount,
    instanceNotificationCount,
    usersNotificationCount,
    isLogouting,
    canEditProject,
    isAdmin,
    demoMode,
    onNotificationUpdate,
    onNotificationMarkAllAs,
    onNotificationDelete,
    onNotificationDeleteAll,
    onNotificationChangeFilterQuery,
    onLogout,
  }) => {
    const [t] = useTranslation();

    const getPageHeaderTitle = useCallback(() => {
      switch (path) {
        case Paths.ROOT:
          return t('common.dashboard');
        case Paths.SETTINGS:
          return t('common.settings');
        case Paths.SETTINGS_PROFILE:
          return t('common.settingsProfile');
        case Paths.SETTINGS_PREFERENCES:
          return t('common.settingsPreferences');
        case Paths.SETTINGS_PREFERENCES_THEME:
          return t('common.settingsPreferencesTheme');
        case Paths.SETTINGS_ACCOUNT:
          return t('common.settingsAccount');
        case Paths.SETTINGS_AUTHENTICATION:
          return t('common.settingsAuthentication');
        case Paths.SETTINGS_ACCESS_TOKENS:
          return t('common.settingsAccessTokens', 'Access tokens');
        case Paths.SETTINGS_ABOUT:
          return t('common.settingsAbout');
        case Paths.SETTINGS_INSTANCE:
          return t('common.settingsInstance');
        case Paths.SETTINGS_USERS:
          return t('common.settingsUsers');
        case Paths.SETTINGS_PROJECT:
          return t('common.settingsProject');
        case Paths.NOTIFICATIONS:
          return t('common.notifications');
        default:
          return project ? project.name : null;
      }
    }, [path, project, t]);

    return (
      <div className={s.wrapper}>
        <Link to={Paths.ROOT} className={s.logo}>
          <Button style={ButtonStyle.HeaderLogo} title={t('common.dashboard')}>
            <img src={logo} alt="Advran" className={s.logoIcon} />
          </Button>
        </Link>
        <div className={s.title} title={getPageHeaderTitle()}>
          {getPageHeaderTitle()}
        </div>
        {demoMode && (
          <div className={s.demoMode}>
            <span className={s.demoModeNotice}>{t('common.demoMode')}</span>
          </div>
        )}
        <div className={s.menuRight}>
          <Link to={Paths.SETTINGS} className={s.hideOnSmall}>
            <Button style={ButtonStyle.Header} title={t('common.settings')}>
              <Icon type={IconType.Settings} size={IconSize.Size18} />
              {instanceNotificationCount > 0 && <span className={s.notification}>{instanceNotificationCount}</span>}
            </Button>
          </Link>
          {isAdmin && (
            <Link to={Paths.SETTINGS_USERS} className={s.hideOnSmall}>
              <Button style={ButtonStyle.Header} title={t('common.settingsUsers')}>
                <Icon type={IconType.Users} size={IconSize.Size18} />
                {usersNotificationCount > 0 && <span className={s.notification}>{usersNotificationCount}</span>}
              </Button>
            </Link>
          )}
          <NotificationsPopup
            items={notifications}
            filteredItems={filteredNotifications}
            filter={notificationFilter}
            onUpdate={onNotificationUpdate}
            onMarkAllAs={onNotificationMarkAllAs}
            onDelete={onNotificationDelete}
            onDeleteAll={onNotificationDeleteAll}
            onChangeFilterQuery={onNotificationChangeFilterQuery}
            hideCloseButton
          >
            <Button style={ButtonStyle.Header} title={t('common.notifications')}>
              <Icon type={IconType.Bell} size={IconSize.Size18} />
              {notificationCount > 0 && <span className={s.notification}>{notificationCount}</span>}
            </Button>
          </NotificationsPopup>
          <UserPopup canEditProject={canEditProject} projectId={project?.id} isAdmin={isAdmin} isLogouting={isLogouting} onLogout={onLogout}>
            <Button style={ButtonStyle.Header} title={t('common.profileAndSettings')}>
              <User name={user.name} avatarUrl={user.avatarUrl} size="card" skipTitle />
            </Button>
          </UserPopup>
        </div>
      </div>
    );
  },
);

Header.propTypes = {
  path: PropTypes.string,
  project: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  notifications: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  filteredNotifications: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  notificationFilter: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  notificationCount: PropTypes.number.isRequired,
  instanceNotificationCount: PropTypes.number.isRequired,
  usersNotificationCount: PropTypes.number.isRequired,
  isLogouting: PropTypes.bool.isRequired,
  canEditProject: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  demoMode: PropTypes.bool.isRequired,
  onNotificationUpdate: PropTypes.func.isRequired,
  onNotificationMarkAllAs: PropTypes.func.isRequired,
  onNotificationDelete: PropTypes.func.isRequired,
  onNotificationDeleteAll: PropTypes.func.isRequired,
  onNotificationChangeFilterQuery: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

Header.defaultProps = {
  path: undefined,
  project: undefined,
};

export default Header;
